// ---------------------------------------------------------------------------
// Kalender (PF 16): Daten (`JJJJ-MM-TTThh:mm`) gelten im Kalender der Geschichte – dem gregorianischen oder einem eigenen mit eigenen
// Monaten, Wochentagen, Jahreszählung und Schalttag. Gespeichert wird weiter Jahr, Monat und Tag, so wie der Text sie nennt („am 12.
// Frostmond 312“ → 0312-03-12); ändert sich der Kalender, bleiben die Angaben, nur ihre Lage in der Zeit folgt ihm.
//
// Zum Rechnen wird ein Datum zu Millisekunden auf einer gleichmäßigen Achse: im gregorianischen Kalender wie bisher (UTC), in einem
// eigenen ab dem ersten Tag des Jahres 0 (ein Tag = 86 400 000). Welcher Kalender gilt, ist der „aktive“: im Browser der der offenen
// Geschichte (`setActiveCalendar`), auf dem Server der der Geschichte der Anfrage (`setCalendarSource`), für eine Rechnung dazwischen
// `withCalendar`. Alles darüber (Zeitlinie, Zeitmarken, Erkennung, Anzeige) fragt nur diese Grundfunktionen.
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import type { Calendar, TimeDisplay } from './types.js';

// ---------------------------------------------------------------------------
// Welcher Kalender gilt
// ---------------------------------------------------------------------------

let scoped: Calendar | null | undefined;
let source: (() => Calendar | null | undefined) | null = null;
let current: Calendar | null = null;

/** Der Kalender, der gerade gilt (null = gregorianisch): eine laufende `withCalendar`-Rechnung, sonst die Quelle, sonst der gesetzte */
export function activeCalendar(): Calendar | null {
  if (scoped !== undefined) return scoped;
  const s = source?.();
  return s !== undefined ? s : current;
}

/** `fn` im Kalender `cal` rechnen (synchron) */
export function withCalendar<T>(cal: Calendar | null, fn: () => T): T {
  const prev = scoped;
  scoped = cal;
  try {
    return fn();
  } finally {
    scoped = prev;
  }
}

/** Der Kalender der offenen Geschichte (Browser) */
export const setActiveCalendar = (cal: Calendar | null): void => {
  current = cal;
};

/** Woher der Kalender kommt (Server: die Geschichte der Anfrage); `undefined` aus der Quelle = der gesetzte */
export const setCalendarSource = (fn: (() => Calendar | null | undefined) | null): void => {
  source = fn;
};

/** Der Kalender einer Geschichte (an ihrer Wurzel) */
export const calendarOf = (ctx: StoryCtx): Calendar | null => ctx.nodes.get(ctx.story.rootId)?.calendar ?? null;

// ---------------------------------------------------------------------------
// Einen Kalender anlegen und prüfen
// ---------------------------------------------------------------------------

export const CALENDAR_LIMITS = { months: 36, monthDays: 400, weekdays: 30, name: 60, era: 20, leapEvery: 1000 } as const;

/** Ein neuer eigener Kalender zum Umbenennen: zwölf Monate zu 30 Tagen, sieben Wochentage */
export function newCalendar(): Calendar {
  const ordinals = ['Erster', 'Zweiter', 'Dritter', 'Vierter', 'Fünfter', 'Sechster', 'Siebter', 'Achter', 'Neunter', 'Zehnter', 'Elfter', 'Zwölfter'];
  return {
    name: 'Eigener Kalender',
    months: ordinals.map((o) => ({ name: `${o} Mond`, days: 30 })),
    weekdays: ['Ersttag', 'Zweittag', 'Dritttag', 'Vierttag', 'Fünfttag', 'Sechsttag', 'Siebttag'],
    firstWeekday: 0,
    era: '',
    leap: null,
  };
}

const NAME_OK = /^[\p{L}][\p{L}\p{M}\p{N}'’ .-]*$/u;

/** Prüft einen Kalender; liefert eine Meldung oder null */
export function checkCalendar(c: Calendar): string | null {
  const L = CALENDAR_LIMITS;
  if (typeof c.name !== 'string' || c.name.length > L.name) return `Der Name des Kalenders hat höchstens ${L.name} Zeichen.`;
  if (!Array.isArray(c.months) || c.months.length < 1 || c.months.length > L.months) return `Ein Kalender hat 1 bis ${L.months} Monate.`;
  if (!Array.isArray(c.weekdays) || c.weekdays.length > L.weekdays) return `Ein Kalender hat höchstens ${L.weekdays} Wochentage.`;
  const names = new Set<string>();
  for (const [i, m] of c.months.entries()) {
    const name = m.name.trim();
    if (!NAME_OK.test(name) || name.length > L.name) return `Monat ${i + 1}: ein Name aus Buchstaben (höchstens ${L.name} Zeichen).`;
    if (!Number.isInteger(m.days) || m.days < 1 || m.days > L.monthDays) return `${name}: 1 bis ${L.monthDays} Tage.`;
    if (names.has(name.toLowerCase())) return `„${name}“ gibt es zweimal.`;
    names.add(name.toLowerCase());
  }
  for (const [i, w] of c.weekdays.entries()) {
    const name = w.trim();
    if (!NAME_OK.test(name) || name.length > L.name) return `Wochentag ${i + 1}: ein Name aus Buchstaben (höchstens ${L.name} Zeichen).`;
    if (names.has(name.toLowerCase())) return `„${name}“ gibt es zweimal.`;
    names.add(name.toLowerCase());
  }
  if (!Number.isInteger(c.firstWeekday) || c.firstWeekday < 0 || (c.weekdays.length > 0 ? c.firstWeekday >= c.weekdays.length : c.firstWeekday !== 0)) return 'Der erste Wochentag gehört zur Woche.';
  if (typeof c.era !== 'string' || c.era.length > L.era || /\d/.test(c.era)) return `Die Jahreszählung ist ein Zusatz ohne Ziffern (höchstens ${L.era} Zeichen).`;
  if (c.leap) {
    if (!Number.isInteger(c.leap.every) || c.leap.every < 1 || c.leap.every > L.leapEvery) return `Ein Schaltjahr kommt alle 1 bis ${L.leapEvery} Jahre.`;
    if (!Number.isInteger(c.leap.month) || c.leap.month < 1 || c.leap.month > c.months.length) return 'Der Schaltmonat gehört zum Kalender.';
  }
  return null;
}

/** Ein Kalender mit bereinigten Namen (ohne Leerraum außen) */
export const tidyCalendar = (c: Calendar): Calendar => ({
  name: c.name.trim(),
  months: c.months.map((m) => ({ name: m.name.trim(), days: m.days })),
  weekdays: c.weekdays.map((w) => w.trim()),
  firstWeekday: c.firstWeekday,
  era: c.era.trim(),
  leap: c.leap ?? null,
});

// ---------------------------------------------------------------------------
// Rechnen im Kalender
// ---------------------------------------------------------------------------

/** Teile eines Datums: Jahr, Monat (1 = erster), Tag (1 = erster), Stunde, Minute */
export interface DateParts {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
}

const DAY_MS = 86_400_000;
const GREGORIAN_MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const GREGORIAN_WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const mod = (a: number, n: number): number => ((a % n) + n) % n;

interface Derived {
  /** Tage je Monat im Gemeinjahr */
  days: number[];
  yearDays: number;
  leapEvery: number;
  /** Schaltmonat (0 = erster), -1 ohne */
  leapMonth: number;
}
const derivedCache = new WeakMap<Calendar, Derived>();
function derived(c: Calendar): Derived {
  let d = derivedCache.get(c);
  if (!d) {
    const days = c.months.map((m) => m.days);
    d = { days, yearDays: days.reduce((a, b) => a + b, 0), leapEvery: c.leap?.every ?? 0, leapMonth: c.leap ? c.leap.month - 1 : -1 };
    derivedCache.set(c, d);
  }
  return d;
}
const isLeap = (d: Derived, y: number): boolean => d.leapEvery > 0 && mod(y, d.leapEvery) === 0;
/** Schaltjahre in [0, y) (negativ für Jahre vor 0) */
const leapsBefore = (d: Derived, y: number): number => (d.leapEvery <= 0 ? 0 : y > 0 ? Math.floor((y - 1) / d.leapEvery) + 1 : -Math.floor(-y / d.leapEvery));
const yearStart = (d: Derived, y: number): number => y * d.yearDays + leapsBefore(d, y);
const monthLength = (d: Derived, y: number, mo: number): number => d.days[mo - 1]! + (mo - 1 === d.leapMonth && isLeap(d, y) ? 1 : 0);

/** Tag (ab dem ersten Tag des Jahres 0) eines Datums im eigenen Kalender */
function dayIndex(d: Derived, y: number, mo: number, day: number): number {
  let idx = yearStart(d, y);
  for (let m = 1; m < mo; m++) idx += monthLength(d, y, m);
  return idx + day - 1;
}

function fromDayIndex(d: Derived, idx: number): { y: number; mo: number; d: number } {
  let y = Math.floor(idx / (d.yearDays + (d.leapEvery > 0 ? 1 / d.leapEvery : 0)));
  while (yearStart(d, y) > idx) y--;
  while (yearStart(d, y + 1) <= idx) y++;
  let rest = idx - yearStart(d, y);
  let mo = 1;
  while (mo < d.days.length && rest >= monthLength(d, y, mo)) rest -= monthLength(d, y, mo++);
  return { y, mo, d: rest + 1 };
}

/** Gregorianisch: Millisekunden (UTC) – auch für die Jahre 0 bis 99 (die Date.UTC sonst als 1900 bis 1999 liest) */
const gregorianMs = (y: number, mo: number, d: number, h: number, mi: number): number => {
  const t = new Date(Date.UTC(2000, mo - 1, d, h, mi));
  t.setUTCFullYear(y);
  return t.getTime();
};

/** Ist es der gregorianische Kalender? */
export const isGregorian = (): boolean => activeCalendar() === null;

/** Monate im Jahr */
export const monthsInYear = (): number => activeCalendar()?.months.length ?? 12;

/** Tage im Monat `mo` des Jahres `y` */
export function daysInMonth(y: number, mo: number): number {
  const c = activeCalendar();
  if (!c) return new Date(gregorianMs(y, mo + 1, 0, 0, 0)).getUTCDate();
  return mo >= 1 && mo <= c.months.length ? monthLength(derived(c), y, mo) : 0;
}

/** Höchstens so viele Tage hat der Monat `mo` (in irgendeinem Jahr) */
export function maxDaysInMonth(mo: number): number {
  const c = activeCalendar();
  if (!c) return mo === 2 ? 29 : [4, 6, 9, 11].includes(mo) ? 30 : 31;
  const d = derived(c);
  return mo >= 1 && mo <= c.months.length ? d.days[mo - 1]! + (mo - 1 === d.leapMonth ? 1 : 0) : 0;
}

/**
 * Datum → Millisekunden auf der Achse des Kalenders. Im eigenen Kalender nur gültige Daten (Monat und Tag im Kalender, Jahr ab 0);
 * gregorianisch wie bisher (ein Überlauf läuft in den nächsten Monat). null, wenn es keins ist.
 */
export function partsToMs(p: DateParts): number | null {
  if (p.h < 0 || p.h > 23 || p.mi < 0 || p.mi > 59) return null;
  const c = activeCalendar();
  if (!c) {
    const ms = gregorianMs(p.y, p.mo, p.d, p.h, p.mi);
    return Number.isFinite(ms) ? ms : null;
  }
  const d = derived(c);
  if (!Number.isInteger(p.y) || p.y < 0 || p.mo < 1 || p.mo > c.months.length || p.d < 1 || p.d > monthLength(d, p.y, p.mo)) return null;
  return (dayIndex(d, p.y, p.mo, p.d) * 1440 + p.h * 60 + p.mi) * 60000;
}

/** Millisekunden → Datum (auf die Minute gerundet) */
export function msToParts(ms: number): DateParts {
  const c = activeCalendar();
  const minutes = Math.round(ms / 60000);
  if (!c) {
    const t = new Date(minutes * 60000);
    return { y: t.getUTCFullYear(), mo: t.getUTCMonth() + 1, d: t.getUTCDate(), h: t.getUTCHours(), mi: t.getUTCMinutes() };
  }
  const day = Math.floor(minutes / 1440);
  const rest = minutes - day * 1440;
  return { ...fromDayIndex(derived(c), day), h: Math.floor(rest / 60), mi: rest % 60 };
}

/** `ms` plus `n` Monate (gregorianisch wie bisher mit Überlauf; im eigenen Kalender bleibt der Tag im Monat, höchstens sein letzter) */
export function addMonths(ms: number, n: number): number {
  const c = activeCalendar();
  if (!c) {
    const t = new Date(ms);
    t.setUTCMonth(t.getUTCMonth() + n);
    return t.getTime();
  }
  const p = msToParts(ms);
  const total = p.y * c.months.length + (p.mo - 1) + n;
  const y = Math.floor(total / c.months.length);
  const mo = mod(total, c.months.length) + 1;
  const d = derived(c);
  return (dayIndex(d, y, mo, Math.min(p.d, monthLength(d, y, mo))) * 1440 + p.h * 60 + p.mi) * 60000;
}

/** `ms` plus `n` Jahre */
export function addYears(ms: number, n: number): number {
  const c = activeCalendar();
  if (!c) {
    const t = new Date(ms);
    t.setUTCFullYear(t.getUTCFullYear() + n);
    return t.getTime();
  }
  return addMonths(ms, n * c.months.length);
}

/** Tage einer Woche (ohne eigene Wochentage: sieben) */
export const weekLength = (): number => activeCalendar()?.weekdays.length || 7;

/** Mittlere Länge eines Jahres bzw. Monats in Tagen (für Abstände und Beschriftungen) */
export function averageYearDays(): number {
  const c = activeCalendar();
  if (!c) return 365.25;
  const d = derived(c);
  return d.yearDays + (d.leapEvery > 0 ? 1 / d.leapEvery : 0);
}
export const averageMonthDays = (): number => averageYearDays() / monthsInYear();

/** Name des Monats `mo` (1 = erster) */
export const monthName = (mo: number): string => (activeCalendar()?.months.map((m) => m.name) ?? GREGORIAN_MONTHS)[mo - 1] ?? String(mo);

/** Namen der Monate */
export const monthNames = (): string[] => activeCalendar()?.months.map((m) => m.name) ?? GREGORIAN_MONTHS;

/** Namen der Wochentage (gregorianisch ab Montag) */
export const weekdayNames = (): string[] => activeCalendar()?.weekdays ?? GREGORIAN_WEEKDAYS;

/** Wochentag eines Zeitpunkts (Index in `weekdayNames`); null, wenn der Kalender keine Woche hat */
export function weekdayIndex(ms: number): number | null {
  const c = activeCalendar();
  const day = Math.floor(Math.round(ms / 60000) / 1440);
  if (!c) return mod(day + 3, 7); // 1.1.1970 war ein Donnerstag
  if (c.weekdays.length === 0) return null;
  const d = derived(c);
  return mod(day - yearStart(d, 1) + c.firstWeekday, c.weekdays.length);
}

/** Name des Wochentags eines Zeitpunkts; null ohne Woche */
export function weekdayName(ms: number): string | null {
  const i = weekdayIndex(ms);
  return i === null ? null : (weekdayNames()[i] ?? null);
}

/** Die Jahreszählung des Kalenders („n. F.“); leer: keine */
export const calendarEra = (): string => activeCalendar()?.era ?? '';

/** Jahreszahl mit der Jahreszählung des Kalenders („312 n. F.“) */
export function yearText(y: number): string {
  const era = calendarEra();
  return era ? `${y} ${era}` : String(y);
}

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * Ein Datum im eigenen Kalender in Worten, begrenzt auf die Teile der Zeitanzeige: „12. Frostmond 312 n. F. 08:00“, „Frostmond 312“,
 * „12. Frostmond“, „312 n. F.“.
 */
export function calendarDateText(p: DateParts, display: TimeDisplay, withTime: boolean): string {
  const parts: string[] = [];
  if (display.day && display.month) parts.push(`${p.d}. ${monthName(p.mo)}`);
  else if (display.month) parts.push(monthName(p.mo));
  else if (display.day) parts.push(`${p.d}.`);
  if (display.year) parts.push(yearText(p.y));
  if (display.time && withTime) parts.push(`${pad(p.h)}:${pad(p.mi)}`);
  return parts.join(' ');
}

/** Schritt eines Tages auf der Achse (Millisekunden) */
export const DAY_LENGTH_MS = DAY_MS;
